import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Container,
  Flex,
  Group,
  Text,
  useComputedColorScheme,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { Moon, Sun } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getPostLoginPath } from '../../utils/postLoginNavigate'
import { isAccessTokenUnexpired, userFromAccessToken } from '../../utils/accessToken'

const COLORS = {
  eveningSea: 'var(--sat-green)',
  sunkenShip: 'var(--sat-text)',
  white: 'var(--mantine-color-white)',
}

function useHeaderColors() {
  const theme = useMantineTheme()
  const computed = useComputedColorScheme('dark')
  const isDark = computed === 'dark'

  return {
    text: isDark ? theme.white : COLORS.sunkenShip,
    mutedText: isDark ? theme.colors.gray[3] : COLORS.eveningSea,
    header: isDark ? theme.colors.dark[7] : COLORS.white,
  }
}

export function PublicLandingHeader() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, token } = useAuth()
  const { toggleColorScheme } = useMantineColorScheme()
  const computed = useComputedColorScheme('dark')
  const colors = useHeaderColors()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    onScroll()
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const goHome = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    navigate('/')
  }

  const goToSection = (id: string) => {
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      return
    }

    navigate({ pathname: '/', hash: `#${id}` })
  }

  // Valid JWT in storage means signed-in, even while `/auth/me` is in flight
  const showDashboardCta = Boolean(user) || (token != null && isAccessTokenUnexpired(token))

  return (
    <Box
      component="header"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: colors.header,
        boxShadow: scrolled
          ? `0 4px 20px var(--sat-shadow-strong)`
          : `0 1px 4px var(--sat-shadow-soft)`,
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <Container size="xl" py={14}>
        <Flex justify="space-between" align="center">
          <Text
            fw={800}
            fz={22}
            style={{ color: colors.text, letterSpacing: '-0.5px', cursor: 'pointer' }}
            onClick={goHome}
          >
            SAT Tutor
          </Text>

          <Group gap="xl" visibleFrom="sm">
            <Anchor
              fz="sm"
              fw={500}
              c={colors.mutedText}
              underline="never"
              style={{ cursor: 'pointer' }}
              onClick={() => goToSection('mission')}
            >
              Mission
            </Anchor>
            <Anchor
              fz="sm"
              fw={500}
              c={colors.mutedText}
              underline="never"
              style={{ cursor: 'pointer' }}
              onClick={() => goToSection('features')}
            >
              Features
            </Anchor>
            <Anchor
              fz="sm"
              fw={500}
              c={colors.mutedText}
              underline="never"
              style={{ cursor: 'pointer' }}
              onClick={() => goToSection('pricing')}
            >
              Pricing
            </Anchor>
          </Group>

          <Group gap="sm">
            <Button
              variant="outline"
              color={COLORS.eveningSea}
              radius="xl"
              size="sm"
              onClick={() => {
                if (user) {
                  navigate(getPostLoginPath(user))
                  return
                }
                if (token && isAccessTokenUnexpired(token)) {
                  const u = userFromAccessToken(token)
                  navigate(u ? getPostLoginPath(u) : '/dashboard')
                  return
                }
                navigate('/login')
              }}
            >
              {showDashboardCta ? 'Dashboard' : 'Log in'}
            </Button>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => toggleColorScheme()}
              title="Toggle color scheme"
              style={{ color: colors.text }}
            >
              {computed === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </ActionIcon>
          </Group>
        </Flex>
      </Container>
    </Box>
  )
}
